'use client';

import { useState, useRef, useCallback } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';

type SortType = 'newest' | 'views' | null;

export default function ContentListControls() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const currentQ = searchParams.get('q') || '';
  const currentSort = searchParams.get('sort') as SortType | null;

  const [inputValue, setInputValue] = useState(currentQ);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedReplace = useCallback(
    (value: string, sort: SortType) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const params = new URLSearchParams();
        if (value) params.set('q', value);
        if (sort) params.set('sort', sort);
        router.replace(`${pathname}?${params.toString()}`, { scroll: false });
      }, 300);
    },
    [pathname, router],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedReplace(value, currentSort);
  };

  function buildHref(sort: SortType, q: string): string {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (sort) params.set('sort', sort);
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }

  return (
    <>
      <div className="relative w-full max-w-md">
        <svg
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
          xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="search"
          placeholder="Bu kategoride ara..."
          value={inputValue}
          onChange={handleInputChange}
          className="w-full rounded-full border border-stone-200 bg-white py-2.5 pl-11 pr-4 text-sm text-stone-900 outline-none transition-all duration-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
        />
      </div>
      <div className="flex gap-2">
        <Link
          href={buildHref('newest', currentQ)}
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 sm:text-sm ${
            currentSort === null || currentSort === 'newest'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'border border-stone-200 bg-white text-stone-600 hover:border-stone-300'
          }`}
        >
          En Yeni
        </Link>
        <Link
          href={buildHref('views', currentQ)}
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 sm:text-sm ${
            currentSort === 'views'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'border border-stone-200 bg-white text-stone-600 hover:border-stone-300'
          }`}
        >
          En Çok Okunan
        </Link>
      </div>
    </>
  );
}
